import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config";
import { SceneType } from "store/types/project";
import { MAX_FILE_SIZE } from "./CreateProjectSheet.constants";
import { handleToast } from "utils/toasts";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import {
  PROJECT_CREATE_TIMEOUT_MS,
  type ParsedContent,
  type ParsedSceneLoose,
} from "services/createProjectTask";

const getFileExt = (fileName?: string): string | undefined => {
  if (!fileName) return undefined;
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : undefined;
};

const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export const FILE_TOO_LARGE_MESSAGE = `That file is over ${MAX_FILE_SIZE_MB}MB. Try exporting a smaller PDF, or upload an FDX or Fountain file.`;

/** Human copy for a react-dropzone reject code; its own messages list every accepted MIME type. */
const describeFileRejection = (code: string): string => {
  switch (code) {
    case "file-too-large":
      return FILE_TOO_LARGE_MESSAGE;
    case "too-many-files":
      return "Upload one screenplay at a time.";
    case "file-invalid-type":
    default:
      return "That file type is not supported. Upload a PDF, FDX, Celtx, or Fountain file.";
  }
};

/** Bucketed so we can see size-related failures without recording anything identifying. */
const fileSizeBucket = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return "<1MB";
  if (mb < 5) return "1-5MB";
  if (mb < 10) return "5-10MB";
  if (mb < 20) return "10-20MB";
  return ">20MB";
};

const classifyParseMessage = (message: string): string => {
  const lower = message.toLowerCase();
  if (lower.includes("unsupported file type")) return "unsupported_file_type";
  if (lower.includes("file size") || lower.includes("too large") || lower.includes("5mb")) {
    return "file_too_large";
  }
  if (lower.includes("network") || lower.includes("timeout")) return "network";
  if (lower.includes("invalid file")) return "invalid_file";
  return "parse_failed";
};

export const useFileUpload = (workspaceId?: string | null) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<ParsedContent | null>(null);
  const axios = useAxios();
  const { trackEvent } = useAnalytics();

  type FileRejectionLike = Array<{
    file?: { name?: string };
    errors: Array<{ message: string; code?: string }>;
  }>;

  const fileUploadMutation = useMutation({
    mutationFn: async (data: {
      acceptedFiles: File[];
      fileRejections: FileRejectionLike;
      mode?: string;
      existingProject?: unknown;
    }) => {
      const { acceptedFiles, fileRejections } = data;

      if (fileRejections?.length > 0) {
        const first = fileRejections[0];
        const rejectCode = first?.errors?.[0]?.code || "file-invalid-type";
        const rejectedName =
          (first as { file?: { name?: string } } | undefined)?.file?.name || acceptedFiles[0]?.name;
        trackEvent(ANALYTICS_EVENTS.SCREENPLAY_DROP_REJECTED, {
          reject_reason: rejectCode,
          file_ext: getFileExt(rejectedName),
          mode: data.mode || "project",
        });
        // react-dropzone's own copy lists every accepted MIME type, which reads as noise.
        throw new Error(describeFileRejection(rejectCode));
      }

      const uploadedFile = acceptedFiles?.[0];
      if (!uploadedFile) {
        throw new Error("No file detected. Try selecting the file instead of dragging it.");
      }

      if (uploadedFile.size > MAX_FILE_SIZE) {
        throw new Error(FILE_TOO_LARGE_MESSAGE);
      }

      trackEvent(ANALYTICS_EVENTS.SCREENPLAY_FILE_ATTACHED, {
        file_ext: getFileExt(uploadedFile.name),
        size_bucket: fileSizeBucket(uploadedFile.size),
        mode: data.mode || "project",
      });

      const formData = new FormData();
      formData.append("file", uploadedFile);

      trackEvent(ANALYTICS_EVENTS.SCREENPLAY_PARSE_STARTED, {
        file_ext: getFileExt(uploadedFile.name),
        mode: data.mode || "project",
      });

      const response = await axios.post(USER_API.parseFile, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: {
          mode: data.mode || "project",
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        },
        timeout: PROJECT_CREATE_TIMEOUT_MS,
      });

      const parsed: ParsedContent = response.data as ParsedContent;

      // Validate scene locations
      const scenes = Array.isArray(parsed.scenes) ? parsed.scenes : [];
      scenes.forEach((scene) => {
        const loc = (scene as ParsedSceneLoose)?.location as unknown;
        if (typeof loc === "string" && loc.length > 255) {
          throw new Error("Scene location too long");
        }
      });

      trackEvent(ANALYTICS_EVENTS.SCREENPLAY_PARSE_SUCCEEDED, {
        file_ext: getFileExt(uploadedFile.name),
        scene_count: scenes.length,
        page_count: parsed?.page_count ?? null,
        mode: data.mode || "project",
      });

      return {
        file: uploadedFile,
        parsedData: parsed,
      };
    },
    onSuccess: (data) => {
      setFile(data.file);
      // Sanitize parsed data to avoid null scene/location issues downstream
      const sanitized = (() => {
        const scenes = Array.isArray(data.parsedData?.scenes) ? data.parsedData.scenes : [];
        const cleanScenes: SceneType[] = scenes
          .filter((s): s is NonNullable<ParsedSceneLoose> => Boolean(s) && typeof s === "object")
          .map((s) => {
            // Backend rule: send non-empty string OR null/omit; never send ""
            const primary = typeof s.location === "string" ? s.location.trim() : "";
            const legacy = typeof s._location === "string" ? s._location.trim() : "";
            const locationField: string | null =
              primary.length > 0 ? primary : legacy.length > 0 ? legacy : null;
            const result: SceneType = {
              type: String((s as Partial<SceneType>).type || ""),
              scene_heading: String((s as Partial<SceneType>).scene_heading || ""),
              int_ext: String((s as Partial<SceneType>).int_ext || ""),
              scene_number: (s as Partial<SceneType>).scene_number ?? null,
              sequence: (s as Partial<SceneType>).sequence ?? null,
              time: ((s as Partial<SceneType>).time as string | null) ?? null,
              location: locationField,
              page: ((s as Partial<SceneType>).page as string | null) ?? null,
              length: ((s as Partial<SceneType>).length as string | null) ?? null,
              paragraphs: s.paragraphs || [],
            };
            return result;
          });
        return { ...data.parsedData, scenes: cleanScenes } as ParsedContent;
      })();
      setParsedData(sanitized);
      setFileError(null);
    },
    onError: (error: unknown, variables) => {
      let errorMessage = "Failed to parse screenplay. Please check the file format.";

      const maybeAxios = error as
        | {
            response?: { status?: number; data?: { message?: string; code?: string } };
            code?: string;
          }
        | undefined;
      if (maybeAxios?.response?.data?.message) {
        errorMessage = maybeAxios.response.data.message;
      } else if (error instanceof Error) {
        if (error.message.includes("Network Error") || error.message.includes("timeout")) {
          errorMessage =
            "Network connection issue. Please check your internet connection and try again.";
        } else if (error.message.includes("413") || error.message.includes("Payload Too Large")) {
          errorMessage = FILE_TOO_LARGE_MESSAGE;
        } else {
          errorMessage = error.message;
        }
      }

      const fileName = (variables as { acceptedFiles?: File[] })?.acceptedFiles?.[0]?.name;
      const messageClass = classifyParseMessage(errorMessage);
      // Drop rejects already emit SCREENPLAY_DROP_REJECTED; avoid double-counting as parse_failed.
      // Keyed off the rejections themselves: matching on message text never fired, because
      // react-dropzone says "File type must be ..." rather than anything containing "invalid file".
      const isDropReject = Boolean(
        (variables as { fileRejections?: unknown[] })?.fileRejections?.length
      );

      if (!isDropReject) {
        trackEvent(ANALYTICS_EVENTS.SCREENPLAY_PARSE_FAILED, {
          http_status: maybeAxios?.response?.status,
          api_error_code: maybeAxios?.response?.data?.code,
          message_class: messageClass,
          file_ext: getFileExt(fileName),
          mode: (variables as { mode?: string })?.mode || "project",
          axios_code: maybeAxios?.code,
        });
        handleToast(errorMessage, "error");
      }

      setFileError(errorMessage);
      setFile(null);
    },
  });

  // Direct mutation call - no need for callback wrapper
  const handleFileUpload = useCallback(
    (
      acceptedFiles: File[],
      fileRejections: { errors: Array<{ message: string }> }[],
      mode = "project",
      existingProject?: unknown
    ) => {
      if (fileUploadMutation.isPending) {
        return;
      }
      fileUploadMutation.mutate({ acceptedFiles, fileRejections, mode, existingProject });
    },
    [fileUploadMutation, workspaceId]
  );

  const resetFileState = useCallback(() => {
    setFile(null);
    setFileError(null);
    setParsedData(null);
    fileUploadMutation.reset();
  }, [fileUploadMutation]);

  return {
    file,
    fileLoading: fileUploadMutation.isPending,
    fileError,
    parsedData,
    handleFileUpload,
    resetFileState,
  };
};
